import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs';

import { BasicInterfaceTypeEnum, CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import * as arrObjHelpers from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment, isDayWithinScheduleRule, isSameDay } from '@shared-helpers/date-helpers';
import { addDecimals, limitDecimalDisplay, toCurrencyString } from '@shared-helpers/string-number-helpers';
import { IHttpResponseEmployeeManager } from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { IDefaultValueObject } from '../components/modal/modal-content-default-change/modal-content-default-change.component';
import { ITableLikeLayoutAppearanceOptions } from '../components/table-like-layout/table-like-layout.component';
import { ICatRecord } from '../interfaces/incoming';
import { IAnyCatsType, ITableConfigEntity } from '../interfaces/internal-interfaces';
import { CatsRecord } from '../scripts/catsRecord';
import { Employee } from '../scripts/employee';
import { DatahubService } from '../services/datahub.service';
import { ModalService } from '../services/modal.service';
import { IAnyInterfaceType } from './basicInterface';
import { ContractHandler } from './contracts/contract-handler';
import { EmployeeCatsRecord } from './employeeCatsRecord';
import { Equipment } from './equipment';
import { ExtDocument } from './extDocument';
import { NonbillableCatsRecord } from './NonbillableCatsRecord';
import { PayrollTimesheet } from './PayrollTimesheet';
import { PerDiemCatsRecord } from './PerDiemCatsRecord';
import { ProductionEquipment } from './productionEquipment';
import { ProductionQuantity } from './ProductionQuantity';
import { ProductionText } from './ProductionText';
import { nonbillableDailyConfig, perDiemDailyConfig } from './table-configs/payroll-tables.config';
import { makeDefaultRemapConfig } from './table-configs/table-config-helpers';
import { ITimesheetDate, TimesheetHandler } from './timesheetHandler';

export class DetailPayneHandler {
  public topLine = '';
  public shouldShowBillCodeMappingButton = false;
  public groupedTable: IGroupedTable[] = [];
  public currentType: ITableLikeLayoutAppearanceOptions = 'flat';
  public tableHeading = '';
  public canAddTimesheet = false;
  public isSaveBtnVisible = false;
  public isSaveBtnDisabled = true; //default to be disabled since performing an action will enable it
  public focusedInterFace?: IAnyInterfaceType;
  public focusedTimesheet?: TimesheetHandler;
  public isWorking = false;
  public shouldShowNonbill = false;
  public canBulkAdd = false;
  public isAddPerDiemDisabled = false;
  public isAddNonbillableDisabled = false;
  public isDirectReport = true;

  public shouldShowEmployeeManager = false;
  public employeeManagerInfo$ = new Observable<IHttpResponseEmployeeManager>();

  private perDiemAmountDisplay = toCurrencyString(0);

  public readonly dayButtons: IDayOfWeekButtonEntity[] = [
    { display: 'Sun', value: 'sun', isSelected: false },
    { display: 'Mon', value: 'mon', isSelected: false },
    { display: 'Tue', value: 'tue', isSelected: false },
    { display: 'Wed', value: 'wed', isSelected: false },
    { display: 'Thu', value: 'thu', isSelected: false },
    { display: 'Fri', value: 'fri', isSelected: false },
    { display: 'Sat', value: 'sat', isSelected: false },
  ];

  constructor(
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
  ) {}

  public setTimesheet(timesheet: TimesheetHandler): void {
    this.focusedTimesheet = timesheet;
  }

  private setScreen(configValueArr: IGroupedTable[], options: ITableOptions): void {
    this.isSaveBtnDisabled = true;
    this.tableHeading = options.headLine;
    this.canAddTimesheet = !!options.canAddTimesheet;
    this.isSaveBtnVisible = !!options.isSaveBtnVisible;
    this.shouldShowNonbill = !!options.shouldShowNonBillBtn;
    this.groupedTable = configValueArr;
    this.canBulkAdd = options.canBulkAdd ?? false;
    this.topLine = options.topLine || options.subtext;

    // If we have a timesheet tab selected, but the cell selected has no active cats records
    // we set the timesheet number so the existing logic can run.
    if (this.focusedInterFace?.filteredTimesheet && this.groupedTable.length === 0) {
      this.focusedTimesheet?.addDocument(this.focusedInterFace);
    }
  }

  public showSummary(): void {
    this.currentType = 'summary';
    this.shouldShowBillCodeMappingButton = false;
    this.shouldShowEmployeeManager = false;
    this.isDirectReport = true; //reset this every time so the message doesn't show when it should not
    if (this.focusedTimesheet) {
      const headerText = this.focusedTimesheet.display;
      const values = this.focusedTimesheet.getValues();
      const config = this.focusedTimesheet.getConfig();
      const valueConfig = [this.makeSingleTable(values, config, '', false, false, '', false, 'summary')];
      const options = this.makeTableOptions(false, false, false, false, headerText, '');
      this.setScreen(valueConfig, options);
      this.datahub.unfocusTable();
    }
    this.isWorking = false;
  }

  //billing
  public showBillingEmployee(entity: Employee): void {
    this.shouldShowBillCodeMappingButton = true;
    this.setFocusedInterface(entity);
    const shouldShowSave = arrObjHelpers.arrayIsTruthy<EmployeeCatsRecord | NonbillableCatsRecord | PerDiemCatsRecord>(
      entity.detailPayneRecords,
    );
    if (!shouldShowSave) {
      this.showSimpleMessage(
        entity.display,
        entity.focusedDay?.display ?? '',
        `No labor hours found. Use the Payroll Tab or the Foreman app to add time to this employee.`,
      );
      return;
    }
    const options = this.makeTableOptions(shouldShowSave, false, false, true, entity.display, entity.focusedDay?.display);
    options.topLine = entity.focusedDay?.display ?? '';
    const configValues = this.splitByTimesheet(entity.getBillingDetailPayneConfig(), entity.detailPayneRecords, {
      shouldShowAddBtn: false,
      cssClass: 'cat-show',
      shouldUseEmpty: false, //Prevent client-generated "helper" zero-hour records from appearing in this view
    });
    this.setScreen(configValues, options);
  }

  public showBillingEquipment(entity: Equipment): void {
    this.shouldShowBillCodeMappingButton = true;
    this.setFocusedInterface(entity);
    const options = this.makeTableOptions(true, true, false, true, entity.display, entity.focusedDay?.display);
    const configValues = this.splitByTimesheet(entity.getBillingDetailPayneConfig(), entity.detailPayneRecords, {
      cssClass: 'cat-show',
    });
    this.setScreen(configValues, options);
  }

  public showBillingProductionQuantity(entity: ProductionQuantity | ProductionEquipment): void {
    this.shouldShowBillCodeMappingButton = false;
    this.setFocusedInterface(entity);
    const options = this.makeTableOptions(true, true, false, true, entity.display, entity.focusedDay?.display);
    const configValues = this.splitByTimesheet(entity.getBillingDetailPayneConfig(), entity.detailPayneRecords, {
      cssClass: 'cat-show',
    });
    this.setScreen(configValues, options);
  }

  public showBillingProductionText(entity: ProductionText): void {
    this.shouldShowBillCodeMappingButton = false;
    this.setFocusedInterface(entity);
    const options = this.makeTableOptions(true, true, false, true, entity.display, entity.focusedDay?.display);
    const configValues = this.splitByTimesheet(entity.getBillingDetailPayneConfig(), entity.detailPayneRecords, {
      cssClass: 'cat-show',
    });
    this.setScreen(configValues, options);
  }

  //payroll
  //eslint-disable-next-line complexity -- TODO: split up this logic to make this less of a complex method
  public showPayrollEmployee(entity: Employee, wasNonbillClicked: boolean, wasPerDiemClicked: boolean): void {
    this.shouldShowEmployeeManager = true;
    this.employeeManagerInfo$ = this.datahub.apiService.getEmployeeManager$(entity.EmpId);
    this.setFocusedInterface(entity);
    this.focusedTimesheet?.setApplicableDocumentsToAllDocuments();
    this.isDirectReport = this.datahub.user.isDirectReport(entity.EmpId);
    let groupedTablesForDetailsPane: IGroupedTable[] = [];

    if (wasNonbillClicked) {
      // this.nonbillValues = entity.detailPayneNonbillableRecords
      if (entity.detailPayneNonbillableRecords.length > 0) {
        groupedTablesForDetailsPane = [this.createNonbillableGroupedTable(entity.detailPayneNonbillableRecords)];
      }
    } else if (wasPerDiemClicked) {
      if (this.isDirectReport) {
        this.datahub.payrollTimesheet.findPerDiemTimesheet$(this.datahub.user.approvingId).subscribe({
          next: () => {
            groupedTablesForDetailsPane = [this.createPerDiemGroupedTable(entity.detailPaynePerDiemRecords, entity)];
          },
          error: (err) => {
            console.error('Error finding per diem timesheet:', err);
          },
        });
      }
    } else {
      groupedTablesForDetailsPane = this.splitByTimesheet(
        entity.getPayrollDetailPayneConfig(entity.focusedDay?.number),
        entity.detailPayneRecords,
        { cssClass: 'cat-show', shouldUseEmpty: !wasNonbillClicked },
      );
    }

    const options = this.makeTableOptions(true, true, true, true, entity.display, entity.focusedDay?.display);
    this.isAddPerDiemDisabled = !this.isDirectReport || wasPerDiemClicked || entity.hasPerDiemRecordsForFocusedDay();
    this.isAddNonbillableDisabled = !this.isDirectReport;
    this.setScreen(groupedTablesForDetailsPane, options);
  }

  public showPayrollEquipment(entity: Equipment | ProductionEquipment): void {
    this.setFocusedInterface(entity);
    this.focusedTimesheet?.setApplicableDocumentsToAllDocuments();
    const options = this.makeTableOptions(true, true, false, true, entity.display, entity.focusedDay?.display);
    const configValues = this.splitByTimesheet(entity.getPayrollDetailPayneConfig(), entity.detailPayneRecords, {
      cssClass: 'cat-show',
    });
    this.setScreen(configValues, options);
  }

  public handleAddPerDiemClicked(): void {
    const employee = this.focusedInterFace as Employee;
    this.datahub.payrollTimesheet.findPerDiemTimesheet$(this.datahub.user.approvingId).subscribe({
      next: () => {
        if (this.datahub.user.isDirectReport(employee.EmpId)) {
          this.addPerDiemRows();
        } else {
          this.datahub.messageService.warning(
            "You cannot add a per diem for an employee that doesn't report to you. Please contact this employee's manager to add a per diem.",
          );
        }
      },
      error: (err) => {
        console.error('Error finding per diem timesheet:', err);
      },
    });
  }

  public showPayrollProduction(entity: ProductionQuantity | ProductionText): void {
    this.setFocusedInterface(entity);
    this.focusedTimesheet?.setApplicableDocumentsToAllDocuments();
    const options = this.makeTableOptions(true, true, false, true, entity.display, entity.focusedDay?.display);
    const configValues = this.splitByTimesheet(entity.getPayrollDetailPayneConfig(), entity.detailPayneRecords, {
      cssClass: 'cat-show',
    });
    this.setScreen(configValues, options);
  }

  public showInvalidEntity(entity: Employee | Equipment, description: string) {
    this.setFocusedInterface(entity);
    const options = this.makeTableOptions(false, false, false, false, entity.display, entity.focusedDay?.display);

    const hasPerDiems = entity instanceof Employee && entity.hasPerDiemRecordsForFocusedDay();
    this.isDirectReport = entity instanceof Employee ? this.datahub.user.isDirectReport(entity.EmpId) : true;
    this.isAddPerDiemDisabled = !this.isDirectReport || hasPerDiems;
    this.isAddNonbillableDisabled = !this.isDirectReport;

    this.setScreen(
      [this.makeSingleTable([{}], [{}], `This ${description} is not valid for this date.`, true, false, '', false, '')],
      options,
    );
  }

  private showSimpleMessage(headerText: string, subheader: string, text: string): void {
    const options = this.makeTableOptions(false, false, false, false, headerText, subheader);
    this.setScreen([this.makeSingleTable([{}], [{}], text, true, false, '', false, 'simple-message')], options);
  }

  public showTorRecords(entity: Employee): void {
    this.setFocusedInterface(entity);
    //when add timesheet is clicked, it needs to have the config stored to work. normally that is handled by the splittiemsheet function.
    const options = this.makeTableOptions(true, true, true, true, entity.display, entity.focusedDay?.display);
    const table = this.makeSingleTable(
      entity.getTorValues(entity.focusedDay?.weekDay ?? 'sun'),
      entity.getTorConfig(),
      'This absence was created in the time off request (TOR) application.',
      true,
      false,
      '',
      false,
      '',
      'tor',
    );

    this.isDirectReport = this.datahub.user.isDirectReport(entity.EmpId);
    this.isAddPerDiemDisabled = !this.isDirectReport || entity.hasPerDiemRecordsForFocusedDay();
    this.isAddNonbillableDisabled = !this.isDirectReport;
    this.setScreen([table], options);
  }

  public showBulkAdd(
    entity: Employee | Equipment | ProductionQuantity | ProductionEquipment,
    config: ITableConfigEntity[],
    isPayroll = false,
  ): void {
    this.dayButtons.forEach((dayButton) => (dayButton.isSelected = false));
    this.shouldShowEmployeeManager = isPayroll && entity instanceof Employee;
    if (isPayroll && entity instanceof Employee) {
      this.employeeManagerInfo$ = this.datahub.apiService.getEmployeeManager$(entity.EmpId);
    }
    this.setFocusedInterface(entity);
    this.focusedTimesheet?.setApplicableDocumentsToAllDocuments();
    const options = this.makeTableOptions(true, true, isPayroll, true, entity.display, 'Bulk Add', {
      shouldUseEmpty: true,
      canBulkAdd: true,
      topLine: 'Create Records and select which days to insert them into.',
    });

    //Regardless of the config, a bulk add should never have the ability to move a record
    const moveRecordConfigIndex = config.findIndex((c) => c.clickValue === 'moveTimeSheetClicked');
    if (moveRecordConfigIndex != null) {
      config.splice(moveRecordConfigIndex, 1);
    }

    const configValues = this.splitByTimesheet(config, [], { cssClass: 'cat-show' });

    this.isDirectReport = entity instanceof Employee ? this.datahub.user.isDirectReport(entity.EmpId) : true;
    this.isAddPerDiemDisabled = !this.isDirectReport;
    this.isAddNonbillableDisabled = !this.isDirectReport;

    this.setScreen(configValues, options);
    this.datahub.unfocusTable();
  }

  public handleAddBtnClicked(documentNbr: string, config: ITableConfigEntity[]): CatsRecord {
    const configCopy = structuredClone(config);
    const document = this.focusedTimesheet?.extDocuments.find((x) => x.Documentno === documentNbr);
    const record = this.makeEmptyRow(configCopy) as CatsRecord;
    record.Extdocumentno = document?.Documentno ?? '';
    record.updateProperty('RecOrder', document?.getServiceOrder() ?? '', true);
    return record;
  }

  public handleAddDocumentClicked(): void {
    if (this.focusedInterFace != null) this.focusedTimesheet?.addDocument(this.focusedInterFace);
  }

  public handleNonbillClicked(nonbillCode?: string): NonbillableCatsRecord {
    const employee = this.focusedInterFace as Employee;
    const timesheet = this.focusedTimesheet as PayrollTimesheet;
    let found = this.groupedTable.find((x) => x.type === 'non');
    if (!found) {
      found = this.createNonbillableGroupedTable([]);
      this.groupedTable.push(found);
    }
    const record = employee.addNewNonbillableRecord(timesheet.nonbillableExtDocument?.Documentno ?? '');
    if (nonbillCode) record.updateProperty('AbsAttType', nonbillCode);
    found.values.push(record);
    found.isHidden = false;
    return record;
  }

  private addPerDiemRows(): void {
    const employee = this.focusedInterFace as Employee;
    const isFound = this.groupedTable.some((x) => x.type === 'perDiem');
    if (!isFound) {
      this.groupedTable.push(this.createPerDiemGroupedTable([], employee));
    }
    this.isAddPerDiemDisabled = true;
  }

  public getPerDiemTotals(records: CatsRecord[]): string {
    const totalsArr: number[] = records.map((x) => Number(x.Zperdiemamount));
    const totals = totalsArr.reduce((a, b) => a + b, 0);
    return toCurrencyString(totals, true);
  }

  private createPerDiemGroupedTable(records: PerDiemCatsRecord[], employee: Employee): IGroupedTable {
    this.perDiemAmountDisplay = this.getPerDiemTotals(records);
    records.forEach((rec) => {
      if (Number(rec.Zperdiemamount) > 0) {
        const config = this.datahub.payrollTimesheet.perDiemsConfig.find((perDiem) => perDiem.skf === rec.Statkeyfig);
        rec.updateProperty('PerDiems', config);
        rec.updateProperty('rowSpanOverride', 3, true); //make these Per Diem rows span fewer table rows than others usually do
      } else {
        rec.updateProperty('shouldShow', false);
      }
    });

    //We only get records back that were created, but we need to show all 4 per diem items in the UI
    //So we have to figure out which ones we need to add that we do not already have an SKF for
    const existingSkfs = records.filter((record) => Number(record.Zperdiemamount) > 0).map((r) => r.Statkeyfig);
    const timesheet = this.focusedTimesheet as PayrollTimesheet;
    const blankUiRecordsToAdd = this.datahub.payrollTimesheet.perDiemsConfig
      .filter((config) => !existingSkfs.includes(config.skf))
      .map((config) => {
        const docNum = timesheet.perDiemExtDocument?.Documentno ?? '';
        const newPerDiemRecord = employee.addNewPerDiemRecord(docNum, employee.focusedDay);
        newPerDiemRecord.Statkeyfig = config.skf;
        newPerDiemRecord.updateProperty('PerDiems', config, true);
        newPerDiemRecord.updateProperty('rowSpanOverride', 3, true); //make these Per Diem rows span fewer table rows than others usually do
        return newPerDiemRecord;
      });

    //Now we combine the real records and the "blank" config items, and sort them alphabetically by the SKF since that happens to already be in the order we want them to appear
    const recordsForTable = [...records, ...blankUiRecordsToAdd].sort((a, b) => a.Statkeyfig.localeCompare(b.Statkeyfig));

    return this.makeSingleTable(
      recordsForTable,
      perDiemDailyConfig,
      this.perDiemAmountDisplay,
      false,
      false,
      '',
      false,
      '',
      'perDiem',
    );
  }

  public handleDefaultClicked$(
    focusedInterFace: Employee | Equipment,
    type: BasicInterfaceTypeEnum.Employee | BasicInterfaceTypeEnum.Equipment,
    serviceOrder: ContractHandler,
  ): Observable<void> {
    let values: IDefaultValueObject[] = [];
    const billcodes = serviceOrder.getBillCodesByType(type);
    const config = makeDefaultRemapConfig(billcodes, type);
    if (type === BasicInterfaceTypeEnum.Employee) {
      const employee = focusedInterFace as Employee;
      const activities = employee.generateOvertimeTypesFromActivityType(employee.Default);
      values = activities.map((actType) => {
        const billcode = serviceOrder.getEmployeeDefault(actType.code, employee.EmpId);
        let current = '';
        if (billcode.toUpperCase() === serviceOrder.getBillCodeDescription(billcode).toUpperCase()) {
          current = billcode;
        } else {
          current = `${billcode} \n${serviceOrder.getBillCodeDescription(billcode)}`;
        }
        return {
          label: actType.description,
          activityFirstDigit: actType.singleDigitNumber,
          current,
          currentBillcode: billcode,
          new: billcode,
        };
      });
    } else if (type === BasicInterfaceTypeEnum.Equipment) {
      const equipment = focusedInterFace as Equipment;
      const equipmentBillCode = serviceOrder.getEquipmentDefault(equipment);
      let current = '';
      if (equipmentBillCode.toUpperCase() === serviceOrder.getBillCodeDescription(equipmentBillCode).toUpperCase()) {
        current = equipmentBillCode;
      } else {
        current = `${equipmentBillCode} \n${serviceOrder.getBillCodeDescription(equipmentBillCode)}`;
      }
      values = [
        {
          label: focusedInterFace.display,
          current,
          currentBillcode: equipmentBillCode,
          new: equipmentBillCode,
        },
      ];
    }
    const custName = serviceOrder.value.CustomerName;
    const questionText = `Select default(s) for ${focusedInterFace.display}\n`;
    const questionTextSecondLine = `for ${custName}\n`;
    const data = {
      title: `Change Bill Code Mapping`,
      svgIcon: 'map-with-pin',
      body: questionText + questionTextSecondLine,
      config: config,
      values: values,
    };
    return this.modalService
      .launchResponsiveModal('default-change', data, { autoFocus: 'first-tabbable' })
      .afterClosed()
      .pipe(
        map((results: IDefaultValueObject[]) => {
          if (arrObjHelpers.arrayIsTruthy(results)) {
            const changedDefaults = results.filter((x) => x.current !== x.new);
            changedDefaults.forEach((cd) => {
              this.datahub.contracts.setDefaultBillcode(focusedInterFace, cd, serviceOrder);
            });
            this.modalService.launchResponsiveModal('simple-text', {
              title: `Change Bill Code Mapping`,
              svgIcon: 'map-with-pin',
              body: `You have successfully updated your bill codes for
${focusedInterFace.display} for ${custName}

*REMEMBER*

This only updates the bill code going forward. Time entered in the system before this change will need to be updated manually.
`,
            });
          }
        }),
      );
  }

  private createNonbillableGroupedTable(records: NonbillableCatsRecord[]): IGroupedTable {
    const nonbillTimesheet = this.focusedTimesheet as PayrollTimesheet;
    const nonbillableExtDocument = nonbillTimesheet.nonbillableExtDocument;

    //Note that the `displaySecondary` here will most likely be empty for nonbillable, but we should provide it anyway to be
    const displayStrings = nonbillableExtDocument
      ? [nonbillableExtDocument.display, nonbillableExtDocument.displaySecondary]
      : ['', ''];

    return this.makeSingleTable(records, nonbillableDailyConfig, displayStrings, false, false, '', false, 'cat-show', 'non');
  }

  public save$(
    focusedInterface: Employee | Equipment,
    selectedDaysFromBulkAdd: IDayOfWeekButtonEntity[],
  ): Observable<ICatRecord[]> {
    this.isWorking = true;
    const filteredTable = this.groupedTable.filter((x) => x.type !== 'tor'); //TOR records are not saved in CatsDB, so we need to exclude them from saving here
    const values = filteredTable.flatMap((x) => x.values);
    const categories = this.datahub.catsDB.filterRecordsByOperation(values);
    if (!arrObjHelpers.arrayIsTruthy(categories.all)) {
      this.refreshTimesheet();
      return of([]);
    }
    const extDocumentsNo = arrObjHelpers.findUnique<string>(categories.all, 'Extdocumentno', false);
    if (this.canBulkAdd) {
      if (focusedInterface instanceof Employee) {
        const allRecordsForSave = this.duplicateRecords(selectedDaysFromBulkAdd, categories.create, focusedInterface); //Employee

        const perDiemRecordsForSave = allRecordsForSave.filter((cat) => cat.Zrecordtype === CatsRecordTypeEnum.EmployeePerDiem);
        const recordsForCreate = allRecordsForSave.filter((cat) => cat.Zrecordtype !== CatsRecordTypeEnum.EmployeePerDiem); // start will all non-per diem records, and we will add to this

        if (perDiemRecordsForSave.length > 0) {
          //Per Diem records might update instead of create on a bulk add, so we need to determine that here
          const existingPerDiemRecordsOnEmp = focusedInterface.catsRecords.filter(
            (cat) => cat.Zrecordtype === CatsRecordTypeEnum.EmployeePerDiem,
          );
          const recordsToUpdate: ICatRecord[] = [];
          perDiemRecordsForSave.forEach((record) => {
            const existingPerDiemToUpdate = existingPerDiemRecordsOnEmp.find((rec) => {
              const isPerDiemOnSameDay = isSameDay(convertSapDateToMoment(record.Workdate), convertSapDateToMoment(rec.Workdate));
              return record.Statkeyfig === rec.Statkeyfig && isPerDiemOnSameDay;
            });

            if (existingPerDiemToUpdate == null) {
              recordsForCreate.push(record); //per diem record doesn't already exist, so we are creating it
            } else {
              //Per Diem records will need to ADD values to any existing records instead of creating new records
              record.Counter = existingPerDiemToUpdate.Counter;
              record.Zperdiemamount = limitDecimalDisplay(
                addDecimals(existingPerDiemToUpdate.Zperdiemamount, record.Zperdiemamount),
              );
              recordsToUpdate.push(record);
            }
          });
          categories.update = recordsToUpdate; //If we found any per diem records to update, put them into this list
        }

        categories.create = recordsForCreate;
      } else {
        categories.create = this.duplicateRecords(selectedDaysFromBulkAdd, categories.create); //Equipment
      }
    }

    if (this.focusedInterFace) {
      this.focusedTimesheet?.updateExtDocumentStatus(extDocumentsNo, this.focusedInterFace.billCodeType);
      return this.focusedInterFace?.save$(categories).pipe(
        tap((localCatsRecords) => {
          if (focusedInterface instanceof Employee && this.focusedTimesheet instanceof PayrollTimesheet) {
            this.focusedTimesheet.updateTimesheetEmployeeRecordsFromLocalInterface(localCatsRecords, focusedInterface.EmpId);
          }
          this.refreshTimesheet();
        }),
      );
    }
    return of([]);
  }

  private duplicateRecords(
    dayButtons: IDayOfWeekButtonEntity[],
    catRecordsList: ICatRecord[],
    employee?: Employee,
  ): ICatRecord[] {
    let absCodes: string[] = [];
    if (employee) {
      absCodes = this.datahub.nonBillable.absence.map((a) => a.value);
    }
    const records: ICatRecord[] = [];
    dayButtons.forEach((day) => {
      if (this.focusedTimesheet) {
        const date = this.focusedTimesheet.getDate(day.value);
        catRecordsList.forEach((record) => {
          if (
            record.Zrecordtype === CatsRecordTypeEnum.EmployeePerDiem ||
            this.isDuplicateRecordValidForDate(record, date, absCodes, employee)
          ) {
            const recordCopy = structuredClone(record);
            recordCopy.Workdate = date.unix;
            records.push(recordCopy);
          }
        });
      }
    });

    return records;
  }

  public refreshTimesheet(): void {
    this.datahub.unfocusTable();
    this.focusedTimesheet?.determineTimesheetStatus();
    this.focusedTimesheet?.getValues();
    this.showSummary(); //if this behavior becomes undesirable, you need to make sure the detail payne is re-rendered before removing this.
  }

  private makeEmptyRow(config: ITableConfigEntity[]): IAnyCatsType {
    this.handleDisableRecordFeatures(config);

    return this.focusedInterFace.addNewRecord(config);
  }

  public addDocumentToView(document: ExtDocument, config: ITableConfigEntity[]): void {
    this.handleDisableRecordFeatures(config);

    const newTable = this.makeSingleTable(
      [this.handleAddBtnClicked(document.Documentno, config)],
      config,
      [document.display, document.displaySecondary],
      true,
      false,
      document.Documentno,
      true,
      '',
    );
    this.groupedTable.push(newTable);
  }

  public handleDisableRecordFeatures(tableGroupConfig: ITableConfigEntity[] = []): void {
    //When a record is changed in the UI we need to disable some features until the "Save" button is pressed to avoid confusion
    //This will be re-enabled by default when saving completes and the details pane reloads
    const moveRecordConfig = tableGroupConfig.find((config) => config.clickValue === 'moveTimeSheetClicked');
    if (moveRecordConfig != null) {
      moveRecordConfig.isDisabled = true;
      moveRecordConfig.tooltip = 'Moving this record is not allowed until you save your changes';
    }
  }

  //eslint-disable-next-line complexity -- TODO: split up this logic to make this less of a complex method
  private isDuplicateRecordValidForDate(
    record: ICatRecord,
    date: ITimesheetDate,
    absCodes: string[],
    employee?: Employee,
  ): boolean {
    let isAbsence = false;
    let isDayWithinRule = true;

    //Only allow adding records if it's supposed to be added for a valid contract date range
    //This prevents issues where a bulk add tries to add records for multiple days if a contract ends mid-week
    const doc = this.focusedTimesheet?.applicableExtDocuments.find((x) => x.Documentno === record.Extdocumentno);
    const isValidForDate = doc?.contractDetails?.isValidForDate(date.moment) ?? true; //assume it's valid if we can't find it for some reason!

    if (employee?.WorkScheduleRule) {
      //Employee's only
      //we need to see if what is being added is either a regular item, or a nonbillable absence.
      //If it is nonbillable absence then it must be within the work schedule rule
      isAbsence = absCodes.includes(record.AbsAttType);
      isDayWithinRule = isDayWithinScheduleRule(employee.WorkScheduleRule, date.weekDay);
    }

    return (!isAbsence && isValidForDate) || (isAbsence && isDayWithinRule);
  }

  private setFocusedInterface(entity: Employee | Equipment | ProductionQuantity | ProductionText | ProductionEquipment): void {
    this.currentType = 'flat';
    // this.nonbillValues = []
    this.focusedInterFace = entity;
  }

  private makeTableOptions(
    isSaveBtnVisible: boolean,
    canAddTimesheet: boolean,
    shouldShowNonBillBtn: boolean,
    isGrouped: boolean,
    headLine: string,
    subtext?: string,
    options?: ITableOptions | ISingleTableOptions,
  ): ITableOptions {
    //handles setting the global options and config
    return {
      isSaveBtnVisible,
      canAddTimesheet,
      shouldShowNonBillBtn: shouldShowNonBillBtn,
      isGrouped: isGrouped,
      headLine,
      subtext: subtext ?? '',
      canBulkAdd: options?.canBulkAdd ?? false,
      topLine: options?.topLine,
    };
  }

  //eslint-disable-next-line complexity -- TODO: split up this logic to make this less of a complex method
  private splitByTimesheet(config: ITableConfigEntity[], catsArray: CatsRecord[], options: ISingleTableOptions): IGroupedTable[] {
    const configCopy = structuredClone(config);
    if (options.shouldUseEmpty === undefined) options.shouldUseEmpty = true;
    const interfaceTimesheets = this.focusedInterFace?.visibleCatsRecords.map((x) => x.Extdocumentno) ?? [];
    let applicableDocuments =
      this.focusedTimesheet?.applicableExtDocuments.filter((doc) => {
        //Nonbillable and per diem is handled outside this process, but they get timesheets starting in v23
        return doc.isBillable() && interfaceTimesheets.some((timesheetNo) => doc.Documentno === timesheetNo);
      }) ?? [];

    //This prevents empty items for invalid contacts being added
    //there is no focused day on the bulk add. but if we are on a day of the week we will filter out invalid contracts by date
    const focusedDay = this.focusedInterFace?.focusedDay;
    if (options.shouldUseEmpty && focusedDay) {
      applicableDocuments = applicableDocuments.filter((doc) => doc.contractDetails?.isValidForDate(focusedDay.moment));
    }

    //if the view is filtered by timesheet, then lets not make an empty row for unused timesheets.
    return applicableDocuments.map((document, i) => {
      const shouldHideHeaders = i > 0;
      let shouldHideTable = false;
      let values = catsArray.filter((x) => x.Extdocumentno === document.Documentno);
      if (!arrObjHelpers.arrayIsTruthy(values) && options?.shouldUseEmpty) {
        values = [this.handleAddBtnClicked(document.Documentno, configCopy)]; //if there are no values for the timesheet, lets save them a click and add an empty row.
      }
      if (!arrObjHelpers.arrayIsTruthy(values)) {
        shouldHideTable = true;
      }

      return this.makeSingleTable(
        values,
        configCopy,
        [document.display, document.displaySecondary],
        shouldHideHeaders,
        shouldHideTable,
        document.Documentno,
        options.shouldShowAddBtn ?? true,
        options.cssClass ?? '',
      );
    });
  }

  private makeSingleTable(
    values: any,
    config: any[], //TODO: This should be tableConfigEntity[] but some usages pass in empty objects!
    display: string | string[],
    shouldHideHeader: boolean,
    shouldHideTable: boolean,
    addBtnValue: string,
    shouldShowAddBtn: boolean,
    cssClass: string,
    type: IGroupedTableType = 'cats',
  ): IGroupedTable {
    return {
      values,
      config,
      isHidden: shouldHideTable,
      columns: config.map((x) => x.key),
      headerText: display,
      shouldHideHeader: shouldHideHeader,
      addBtnValue,
      shouldShowAddBtn: shouldShowAddBtn,
      cssClass,
      type,
    };
  }
}

export interface ITableOptions {
  isSaveBtnVisible: boolean;
  canAddTimesheet: boolean;
  shouldShowNonBillBtn: boolean;
  isGrouped: boolean;
  headLine: string;
  subtext: string;
  canBulkAdd?: boolean;
  topLine?: string;
}

export type IGroupedTableType = 'non' | 'cats' | 'tor' | 'perDiem';

export interface IGroupedTable {
  config: ITableConfigEntity<keyof CatsRecord>[]; //describes columns and metadata
  values: CatsRecord[]; //array of values for columns
  columns: string[];
  type: IGroupedTableType;
  isHidden: boolean; //if true, do not show this whole group
  headerText: string | string[]; //Text for header
  addBtnValue: string;
  shouldHideHeader?: boolean | undefined; //control to hide header text
  shouldShowAddBtn: boolean;
  cssClass: string;
}

interface ISingleTableOptions {
  shouldShowAddBtn?: boolean;
  shouldUseEmpty?: boolean;
  cssClass?: string;
  filteredCostCenter?: string;
  canBulkAdd?: boolean;
  topLine?: string;
}

export interface IDayOfWeekButtonEntity {
  value: IDayOfWeek;
  display: string;
  isSelected: boolean;
}
